/**
 * Signed-in connection-invite accept — claim then PUT ACCEPTED (iOS parity).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Spin, Typography, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { inviteCopyEn } from '../../content/invite.en';
import {
  acceptConnectionInvite,
  claimConnectionInvite,
} from '../../services/inviteService';
import { patchAppUserSettings } from '../../services/userPreferencesSyncService';
import styles from './Invite.module.css';

const { Title, Paragraph } = Typography;

type AcceptState =
  | { kind: 'working' }
  | { kind: 'error'; detail: string }
  | { kind: 'done' };

const InviteAccept: React.FC = () => {
  const { inviteId: rawInviteId } = useParams<{ inviteId: string }>();
  const inviteId = rawInviteId?.trim() || '';
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [state, setState] = useState<AcceptState>({ kind: 'working' });
  const ranRef = useRef(false);

  const runAccept = async (): Promise<void> => {
    if (!inviteId) {
      setState({ kind: 'error', detail: inviteCopyEn.notFound });
      return;
    }
    if (!isAuthenticated || !user) {
      setState({ kind: 'error', detail: inviteCopyEn.acceptFailed });
      return;
    }
    const email = (user.email || '').trim();
    if (!email) {
      setState({ kind: 'error', detail: inviteCopyEn.acceptMissingEmail });
      return;
    }

    setState({ kind: 'working' });
    try {
      try {
        patchAppUserSettings({ communityFeaturesEnabled: true });
      } catch {
        // local prefs are best-effort
      }

      await claimConnectionInvite({ inviteId, email });
      const result = await acceptConnectionInvite(inviteId);

      message.success(
        result.alreadyAccepted
          ? inviteCopyEn.acceptAlreadyAccepted
          : inviteCopyEn.acceptSuccess
      );
      setState({ kind: 'done' });
      navigate('/trips/shared', { replace: true });
    } catch {
      setState({ kind: 'error', detail: inviteCopyEn.acceptFailed });
    }
  };

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    void runAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  if (state.kind === 'working' || state.kind === 'done') {
    return (
      <div className={styles.page} data-invite-id={inviteId || undefined}>
        <div className={styles.container}>
          <Spin size="large" tip={inviteCopyEn.acceptLoading} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} data-invite-id={inviteId || undefined}>
      <div className={styles.container}>
        <Title className={styles.title}>{inviteCopyEn.title}</Title>
        <Paragraph className={styles.lead}>{state.detail}</Paragraph>
        <div className={styles.ctaGroup}>
          <Button type="primary" size="large" onClick={() => void runAccept()}>
            {inviteCopyEn.acceptRetry}
          </Button>
          {inviteId ? (
            <Link className={styles.secondaryLink} to={`/invite/${encodeURIComponent(inviteId)}`}>
              {inviteCopyEn.acceptBackToInvite}
            </Link>
          ) : null}
          <Link className={styles.secondaryLink} to="/trips">
            {inviteCopyEn.acceptGoToTrips}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;
